import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { Shield, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadCapture = () => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    service_type: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.service_type) {
      toast.error("Please select a type of legal service.");
      return;
    }
    
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/consultation`, formData);
      toast.success(
        "Thank you! Your consultation request has been submitted. We'll contact you within 24 hours."
      );
      setFormData({
        full_name: "",
        email: "",
        phone: "",
        service_type: "",
        message: "",
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const services = [
    "Property Development & Conveyancing",
    "Notarial Services",
    "Commercial Agreements",
    "Civil Litigation",
    "Corporate Governance",
    "Other",
  ];

  return (
    <section
      id="lead-capture"
      className="bg-white py-24 lg:py-32 relative overflow-hidden"
      data-testid="lead-capture-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A646' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Text */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-4">
              Request a Consultation
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl font-semibold text-charcoal leading-tight tracking-tighter mb-6">
              Discuss Your Property Development or Legal Matter
            </h2>
            <p className="text-charcoal/60 font-sans text-lg leading-relaxed mb-10">
              Whether you need specialist conveyancing for a property development,
              notarial instruments, commercial agreements or governance advisory,
              our team is ready to assist. Fill out the form and we'll get back
              to you within 24 hours.
            </p>

            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-brand-gold/10 flex items-center justify-center mr-4 flex-shrink-0">
                  <Clock className="h-6 w-6 text-brand-gold" />
                </div>
                <div>
                  <h4 className="text-charcoal font-sans font-medium mb-1">
                    24-Hour Response
                  </h4>
                  <p className="text-charcoal/50 font-sans text-sm">
                    We'll respond to your inquiry within one business day.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-12 h-12 bg-brand-gold/10 flex items-center justify-center mr-4 flex-shrink-0">
                  <Shield className="h-6 w-6 text-brand-gold" />
                </div>
                <div>
                  <h4 className="text-charcoal font-sans font-medium mb-1">
                    Confidential & Secure
                  </h4>
                  <p className="text-charcoal/50 font-sans text-sm">
                    Your information is protected by attorney-client privilege.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-light-grey border border-charcoal/10 p-8 lg:p-10 shadow-lg"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-charcoal/70 text-sm mb-2 font-sans">
                  Full Name *
                </label>
                <Input
                  type="text"
                  name="full_name"
                  placeholder="Enter your full name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  required
                  className="w-full py-3 text-charcoal placeholder:text-charcoal/40 bg-white border-charcoal/20 focus:border-brand-gold rounded-none"
                  data-testid="lead-form-name"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-charcoal/70 text-sm mb-2 font-sans">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full py-3 text-charcoal placeholder:text-charcoal/40 bg-white border-charcoal/20 focus:border-brand-gold rounded-none"
                    data-testid="lead-form-email"
                  />
                </div>
                <div>
                  <label className="block text-charcoal/70 text-sm mb-2 font-sans">
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="+27 XX XXX XXXX"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full py-3 text-charcoal placeholder:text-charcoal/40 bg-white border-charcoal/20 focus:border-brand-gold rounded-none"
                    data-testid="lead-form-phone"
                  />
                </div>
              </div>

              <div>
                <label className="block text-charcoal/70 text-sm mb-2 font-sans">
                  Type of Legal Service *
                </label>
                <Select
                  value={formData.service_type}
                  onValueChange={(value) =>
                    setFormData({ ...formData, service_type: value })
                  }
                >
                  <SelectTrigger
                    className="w-full py-3 text-charcoal bg-white rounded-none border-charcoal/20 focus:border-brand-gold"
                    data-testid="lead-form-service"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-charcoal/20">
                    {services.map((service) => (
                      <SelectItem
                        key={service}
                        value={service}
                        className="text-charcoal hover:bg-brand-gold/10 focus:bg-brand-gold/10"
                      >
                        {service}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-charcoal/70 text-sm mb-2 font-sans">
                  Brief Description of Your Matter
                </label>
                <Textarea
                  name="message"
                  placeholder="Tell us about your legal needs..."
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full py-3 text-charcoal placeholder:text-charcoal/40 bg-white border-charcoal/20 focus:border-brand-gold rounded-none resize-none"
                  data-testid="lead-form-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-gold w-full py-4 text-base font-semibold uppercase tracking-wider rounded-none"
                data-testid="lead-form-submit-btn"
              >
                {isSubmitting ? "Submitting..." : "Request Consultation"}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LeadCapture;
